import React, { useState, useEffect, useMemo, useRef } from 'react';
import ResearchLayout from '../../components/ResearchLayout';
import Pagination from '../../components/Pagination';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const AI_RAW_RESPONSE_FIELDS = [
  { key: 'IDnumber', field: 'assessmentId', label: 'Assessment ID' },
  { key: 'acs', field: 'activityScore', label: 'Activity Score' },
  { key: 'als', field: 'alcoholLifestyleScore', label: 'Alcohol Lifestyle Score' },
  { key: 'bgs', field: 'bloodGlucoseScore', label: 'Blood Glucose Score' },
  { key: 'bms', field: 'bodyMassScore', label: 'Body Mass Score' },
  { key: 'bps', field: 'bloodPressureScore', label: 'Blood Pressure Score' },
  { key: 'chp', field: 'clinicalHealthRiskIndicator', label: 'Clinical Health Risk Indicator' },
  { key: 'chps', field: 'clinicalHealthGrade', label: 'Clinical Health Grade' },
  { key: 'ci', field: 'confidenceIndicator', label: 'Confidence Indicator' },
  { key: 'eds', field: 'educationScore', label: 'Education Score' },
  { key: 'fc', field: 'lifestyleRiskIndicator', label: 'Lifestyle Risk Indicator' },
  { key: 'fs', field: 'lifestyleGrade', label: 'Lifestyle Grade' },
  { key: 'lp', field: 'mentalHealthRiskIndicator', label: 'Mental Health Risk Indicator' },
  { key: 'lps', field: 'mentalHealthGrade', label: 'Mental Health Grade' },
  { key: 'mhp', field: 'physicalHealthRiskIndicator', label: 'Physical Health Risk Indicator' },
  { key: 'mhps', field: 'physicalHealthGrade', label: 'Physical Health Grade' },
  { key: 'mos', field: 'moodScore', label: 'Mood Score' },
  { key: 'php', field: 'overallHealthRiskIndicator', label: 'Overall Health Risk Indicator' },
  { key: 'phps', field: 'overallHealthGrade', label: 'Overall Health Grade' },
  { key: 'rhs', field: 'restingHeartScore', label: 'Resting Heart Score' },
  { key: 'sas', field: 'smokingAlcoholScore', label: 'Smoking Alcohol Score' },
  { key: 'sis', field: 'sleepStressScore', label: 'Sleep Stress Score' },
  { key: 'sls', field: 'sleepScore', label: 'Sleep Score' },
  { key: 'sms', field: 'smokingScore', label: 'Smoking Score' },
  { key: 'ti1', field: 'topInsight1', label: 'Top Insight 1' },
  { key: 'ti2', field: 'topInsight2', label: 'Top Insight 2' },
  { key: 'ti3', field: 'topInsight3', label: 'Top Insight 3' },
  { key: 'wcs', field: 'waistCircumferenceScore', label: 'Waist Circumference Score' },
  { key: 'whis', field: 'waistHipScore', label: 'Waist Hip Score' },
  { key: 'whs', field: 'weightHeightScore', label: 'Weight Height Score' },
];

const parseAiRawResponse = (raw) => {
  if (!raw) {
    return {};
  }

  let parsedData = raw;
  if (typeof raw === 'string') {
    try {
      parsedData = JSON.parse(raw);
    } catch (err) {
      parsedData = raw;
    }
  }

  if (!parsedData || typeof parsedData !== 'object' || Array.isArray(parsedData)) {
    return {};
  }

  return AI_RAW_RESPONSE_FIELDS.reduce((acc, fieldDef) => {
    acc[fieldDef.field] = parsedData[fieldDef.key] != null ? parsedData[fieldDef.key] : '';
    return acc;
  }, {});
};

const renderAiRawResponseHeaders = () => AI_RAW_RESPONSE_FIELDS.map((fieldDef) => (
  <th key={fieldDef.field} style={{ minWidth: '140px' }}>{fieldDef.label}</th>
));

const renderAiRawResponseCells = (row, rowIndex) => {
  const parsed = parseAiRawResponse(row.ai_raw_response);
  return AI_RAW_RESPONSE_FIELDS.map((fieldDef, index) => (
    <td key={`ai-raw-${rowIndex}-${fieldDef.field}-${index}`}><small>{parsed[fieldDef.field]}</small></td>
  ));
};

const StatisticalReportsPage = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [selectedCases, setSelectedCases] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [isCasesLoading, setIsCasesLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [questionHeaders, setQuestionHeaders] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [exportType, setExportType] = useState('doctor');
  const [allCases, setAllCases] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [selectedDoctors, setSelectedDoctors] = useState(new Set());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAssignedDoctorsOnly, setShowAssignedDoctorsOnly] = useState(false);
  const [assignedDoctorCount, setAssignedDoctorCount] = useState('');
  const [showFullyAnsweredCasesOnly, setShowFullyAnsweredCasesOnly] = useState(false);
  const [previewRequestKey, setPreviewRequestKey] = useState(0);
  const previewRequestIdRef = useRef(0);
  const requestCacheRef = useRef(new Map());

  // Expand preview data when backend returns `qa_pairs` (one record per case+doctor)
  const expandedPreviewRows = useMemo(() => {
    try {
      if ((exportType === 'all' || exportType === 'doctor') && Array.isArray(previewData) && previewData.length > 0) {
        // backend preview for doctor/all returns rows with `qa_pairs` array
        if (previewData[0] && Array.isArray(previewData[0].qa_pairs)) {
          const rows = [];
          for (const r of previewData) {
            const base = {
              case_id: r.case_id,
              anonymized_doctor_id: r.anonymized_doctor_id,
              protocol_name: r.protocol_name || '',
              weight: r.weight,
              height: r.height,
              patient_completion_percentage: r.patient_completion_percentage,
              ai_overall_score: r.ai_overall_score,
              ai_color_category: r.ai_color_category,
              ai_risk_level: r.ai_risk_level,
              doctor_name: r.doctor_name,
              doctor_role: r.doctor_role,
              physician_score: r.physician_score,
            };
            for (const qa of (r.qa_pairs || [])) {
              rows.push({
                ...base,
                question_id: qa.question_id,
                question_text: qa.question_text,
                answer: qa.answer,
              });
            }
          }
          return rows;
        }
      }
    } catch (e) {
      // fallback to original previewData
    }
    return previewData || [];
  }, [previewData, exportType]);

  // Prefer per-question columns (returned by backend) when available.
  const hasPerQuestionColumns = Array.isArray(questionHeaders) && questionHeaders.length > 0 && Array.isArray(previewData) && previewData.length > 0 && questionHeaders.some(h => Object.prototype.hasOwnProperty.call(previewData[0], h));

  // When per-question columns exist, render previewData directly (one row per case). Otherwise, if backend only provided qa_pairs, expand them into per-question rows.
  const rowsToRender = hasPerQuestionColumns
    ? (previewData || [])
    : ((Array.isArray(previewData) && previewData.length > 0 && previewData[0] && Array.isArray(previewData[0].qa_pairs)) ? expandedPreviewRows : (previewData || []));

  // Load all cases and doctor list on component mount
  useEffect(() => {
    loadCases();
    loadDoctors();
    loadProtocols();
  }, []);

  useEffect(() => {
    if (selectedProtocol === null) {
      return;
    }

    setSelectedCases(new Set());
    setSelectedDoctors(new Set());
    setAssignedDoctorCount('');
    setShowAssignedDoctorsOnly(false);
    loadCases();
  }, [selectedProtocol]);

  const loadProtocols = async () => {
    try {
      const response = await api.get('/protocols');
      if (response.data && response.data.success) {
        setProtocols(response.data.protocols || []);
      } else {
        setProtocols([]);
      }
    } catch (err) {
      console.error('Error loading protocols:', err);
      setProtocols([]);
    }
  };

  const dedupedApiRequest = async (method, url, data) => {
    const requestKey = `${method.toUpperCase()}:${url}:${JSON.stringify(data ?? {})}`;
    const existingRequest = requestCacheRef.current.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const requestPromise = (method === 'get'
      ? api.get(url)
      : api.post(url, data)
    ).finally(() => {
      requestCacheRef.current.delete(requestKey);
    });

    requestCacheRef.current.set(requestKey, requestPromise);
    return requestPromise;
  };

  const loadCases = async () => {
    setLoading(true);
    setIsCasesLoading(true);
    setError('');
    try {
      let url = '/statistics/cases/list';
      const params = new URLSearchParams();
      if (selectedProtocol) {
        params.append('protocol_id', selectedProtocol);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await dedupedApiRequest('get', url);
      if (response.data && response.data.success) {
        setCases(response.data.cases || []);
      } else {
        setCases([]);
        setError('Failed to load cases');
      }
    } catch (err) {
      console.error('Error loading cases:', err);
      setError('Failed to load cases. Please try again later.');
      setCases([]);
    } finally {
      setLoading(false);
      setIsCasesLoading(false);
    }
  };

  const loadDoctors = async (caseIds = []) => {
    try {
      // Build query parameters with proper array serialization
      let url = '/statistics/doctors/list';
      const params = new URLSearchParams();
      
      if (caseIds && caseIds.length > 0) {
        caseIds.forEach(id => params.append('case_ids', id));
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      const response = await api.get(url);
      if (response.data && response.data.success) {
        setDoctors(response.data.doctors || []);
      } else {
        setDoctors([]);
      }
    } catch (err) {
      console.error('Error loading doctors:', err);
      setDoctors([]);
    }
  };

  // Handle case selection
  const toggleCaseSelection = (caseId) => {
    const newSelected = new Set(selectedCases);
    if (newSelected.has(caseId)) {
      newSelected.delete(caseId);
    } else {
      newSelected.add(caseId);
    }
    setSelectedCases(newSelected);
    setPreviewRequestKey((prev) => prev + 1);
  };

  // Load preview data when filters change
  const loadPreviewData = async (caseIds) => {
    const caseIdArray = Array.from(caseIds || []);
    const shouldPreview = Boolean(
      allCases ||
      caseIdArray.length > 0 ||
      selectedDoctors.size > 0 ||
      selectedProtocol !== null ||
      startDate ||
      endDate ||
      showFullyAnsweredCasesOnly
    );
    if (!shouldPreview) {
      setPreviewData([]);
      setQuestionHeaders([]);
      return;
    }

    const requestId = ++previewRequestIdRef.current;
    setLoadingPreview(true);
    try {
      const response = await dedupedApiRequest('post', '/statistics/export/preview', {
        case_ids: caseIdArray,
        all_cases: allCases,
        doctor_ids: Array.from(selectedDoctors),
        protocol_id: selectedProtocol || null,
        start_date: startDate || null,
        end_date: endDate || null,
        export_type: exportType,
        answered_cases_only: showFullyAnsweredCasesOnly,
      });
      if (requestId !== previewRequestIdRef.current) {
        return;
      }
      if (response.data && response.data.success) {
        setPreviewData(response.data.data || []);
        setQuestionHeaders(response.data.question_headers || []);
      } else {
        setPreviewData([]);
        setQuestionHeaders([]);
      }
    } catch (err) {
      if (requestId !== previewRequestIdRef.current) {
        return;
      }
      console.error('Error loading preview data:', err);
      setPreviewData([]);
    } finally {
      if (requestId === previewRequestIdRef.current) {
        setLoadingPreview(false);
      }
    }
  };

  useEffect(() => {
    if (isCasesLoading) {
      return;
    }

    const hasActiveExportFilters = Boolean(
      allCases ||
      selectedCases.size > 0 ||
      selectedDoctors.size > 0 ||
      selectedProtocol !== null ||
      startDate ||
      endDate ||
      showFullyAnsweredCasesOnly
    );
    if (!hasActiveExportFilters) {
      setPreviewData([]);
      setQuestionHeaders([]);
      return;
    }

    const timer = setTimeout(() => {
      loadPreviewData(selectedCases);
    }, 250);

    return () => clearTimeout(timer);
  }, [previewRequestKey, exportType, allCases, selectedDoctors, startDate, endDate, selectedCases, selectedProtocol, isCasesLoading, showFullyAnsweredCasesOnly, showAssignedDoctorsOnly, assignedDoctorCount]);

  const [casesPerPage] = useState(10);
  const filteredCases = useMemo(() => {
    const doctorCount = parseInt(assignedDoctorCount, 10);
    if (!showAssignedDoctorsOnly || Number.isNaN(doctorCount) || doctorCount < 0) {
      return cases;
    }
    return cases.filter((c) => c.assignment_count === doctorCount);
  }, [cases, showAssignedDoctorsOnly, assignedDoctorCount]);
  const canExport = Boolean(
    allCases ||
    selectedCases.size > 0 ||
    selectedDoctors.size > 0 ||
    selectedProtocol !== null ||
    startDate ||
    endDate ||
    showFullyAnsweredCasesOnly
  );
  const totalItems = filteredCases.length;
  const remainingCasesCount = Math.max(0, filteredCases.length - selectedCases.size);

  const buildExportFilename = (extension) => {
    const protocolName = protocols.find((p) => p.id === selectedProtocol)?.name;
    const protocolPart = protocolName
      ? protocolName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
      : selectedProtocol ? `rp${selectedProtocol}` : '';

    const datePart = startDate && endDate ? `${startDate.replace(/-/g, '')}-${endDate.replace(/-/g, '')}` : '';
    const exportLabel = {
      doctor: 'doctor_detail',
      evaluations: 'doctor_summary',
      doctor_ai: 'doctor_ai_summary',
      ai: 'ai_results',
      cases: 'case_summary',
      all: 'combined_export',
    }[exportType] || exportType || 'export';

    const parts = [protocolPart, datePart, exportLabel].filter(Boolean);
    const baseName = parts.join('_') || 'export';
    return `${baseName}.${extension}`;
  };
  const totalPages = Math.max(1, Math.ceil(totalItems / casesPerPage));
  const paginatedCases = filteredCases.slice((currentPage - 1) * casesPerPage, currentPage * casesPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [totalItems]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const selectPageCases = () => {
    const pageIds = paginatedCases.map((c) => c.id);
    const newSelected = new Set(selectedCases);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => newSelected.has(id));

    if (allPageSelected) {
      pageIds.forEach((id) => newSelected.delete(id));
    } else {
      pageIds.forEach((id) => newSelected.add(id));
    }

    setSelectedCases(newSelected);
    setPreviewRequestKey((prev) => prev + 1);
  };

  // Select all cases
  const selectAllCases = () => {
    let newSelected;
    if (selectedCases.size === filteredCases.length && filteredCases.length > 0) {
      newSelected = new Set();
    } else {
      newSelected = new Set(filteredCases.map((c) => c.id));
    }
    setSelectedCases(newSelected);
    setPreviewRequestKey((prev) => prev + 1);
  };

  const toggleDoctorSelection = (doctorId) => {
    const newSelected = new Set(selectedDoctors);
    if (newSelected.has(doctorId)) {
      newSelected.delete(doctorId);
    } else {
      newSelected.add(doctorId);
    }
    setSelectedDoctors(newSelected);
    setPreviewRequestKey((prev) => prev + 1);
  };

  const resetFilters = () => {
    setAllCases(false);
    setSelectedCases(new Set());
    setSelectedDoctors(new Set());
    setStartDate('');
    setEndDate('');
    setAssignedDoctorCount('');
    setPreviewData([]);
    setShowFullyAnsweredCasesOnly(false);
    setPreviewRequestKey((prev) => prev + 1);
    // When resetting filters, show all doctors by default
    setShowAssignedDoctorsOnly(false);
    loadDoctors();
  };

  // Export to CSV
  const exportToCSV = async () => {
    if (!canExport) {
      setError('Please select at least one case or doctor to export');
      return;
    }

    setExporting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.post('/statistics/export/csv', {
        case_ids: Array.from(selectedCases),
        all_cases: allCases,
        doctor_ids: Array.from(selectedDoctors),
        protocol_id: selectedProtocol || null,
        start_date: startDate || null,
        end_date: endDate || null,
        export_type: exportType,
        answered_cases_only: showFullyAnsweredCasesOnly,
      });

      if (response.data && response.data.success) {
        const csvContent = response.data.csv_data;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', response.data.file_name || buildExportFilename('csv'));
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const exportScopeLabel = allCases ? 'filtered scope' : (selectedCases.size > 0 ? `${selectedCases.size} case(s)` : 'current filter');
        setSuccessMessage(`Successfully exported ${exportScopeLabel} to CSV`);
      } else {
        setError(response.data.message || 'Failed to export data');
      }
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export data. Please try again later.');
    } finally {
      setExporting(false);
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    if (!canExport) {
      setError('Please select at least one case or doctor to export');
      return;
    }

    setExporting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.post('/statistics/export/excel', {
        case_ids: Array.from(selectedCases),
        all_cases: allCases,
        doctor_ids: Array.from(selectedDoctors),
        protocol_id: selectedProtocol || null,
        start_date: startDate || null,
        end_date: endDate || null,
        export_type: exportType,
        answered_cases_only: showFullyAnsweredCasesOnly,
      }, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      let filename = buildExportFilename('xlsx');
      try {
        const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
        if (disposition) {
          const match = disposition.match(/filename\*=UTF-8''(.+)$|filename="?([^;\"]+)"?/);
          if (match) {
            filename = decodeURIComponent(match[1] || match[2]);
          }
        }
      } catch (e) {
        // ignore and use fallback
      }
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const exportScopeLabel = allCases ? 'filtered scope' : (selectedCases.size > 0 ? `${selectedCases.size} case(s)` : 'current filter');
      setSuccessMessage(`Successfully exported ${exportScopeLabel} to Excel`);
    } catch (err) {
      console.error('Error exporting Excel:', err);
      setError('Failed to export data. Please try again later.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <ResearchLayout pageTitle="Statistical Reports">
      <div className="container-fluid">
        {/* Page Header */}
        {/* <div className="mb-4">
          <h2>
            <i className="fas fa-download text-success me-2"></i>
            Export Case Data
          </h2>
          <p className="text-muted">Select cases and export evaluation data to CSV or Excel format</p>
        </div> */}

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError('')}
            ></button>
          </div>
        )}

        {/* Success Alert */}
        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {successMessage}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setSuccessMessage('')}
            ></button>
          </div>
        )}

        {/* Export Controls */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-cog me-2"></i>
              Export Options
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <label className="form-label">Export Type</label>
<select
                   className="form-select"
                   value={exportType}
                   onChange={(event) => setExportType(event.target.value)}
                 >
                   <option value="doctor">Doctor Evaluations (Detail)</option>
                   <option value="evaluations">Doctor Evaluations (Summary)</option>
                   <option value="cases">Cases Only</option>
                   <option value="ai">AI Results Only</option>
                   <option value="doctor_ai">Doctor Evaluations + AI Summary</option>
                   <option value="all">All Three Tables (Combined)</option>
                 </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Research Protocol</label>
                <select
                  className="form-select"
                  value={selectedProtocol || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedProtocol(value === '' ? null : Number(value));
                  }}
                >
                  <option value="">(All protocols)</option>
                  {protocols.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              {/* <div className="col-md-2 d-flex align-items-center">
                <div className="form-check mt-3">
                  <input
                    id="exportAllCases"
                    type="checkbox"
                    className="form-check-input"
                    checked={allCases}
                    onChange={() => {
                      setAllCases(!allCases);
                      if (!allCases) {
                        setSelectedCases(new Set());
                      }
                    }}
                  />
                  <label className="form-check-label" htmlFor="exportAllCases">
                    Export All Cases
                  </label>
                </div>
              </div> */}
              <div className="col-md-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </div>
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    id="showFullyAnsweredCasesOnly"
                    type="checkbox"
                    className="form-check-input"
                    checked={showFullyAnsweredCasesOnly}
                    onChange={(e) => setShowFullyAnsweredCasesOnly(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="showFullyAnsweredCasesOnly">
                    Show only cases fully answered by all assigned doctors
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-check">
                  <input
                    id="showAssignedDoctorsOnly"
                    type="checkbox"
                    className="form-check-input"
                    checked={showAssignedDoctorsOnly}
                    onChange={(e) => setShowAssignedDoctorsOnly(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="showAssignedDoctorsOnly">
                    Filter by exact assigned doctor count
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Assigned Doctor Count</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="form-control"
                  value={assignedDoctorCount}
                  onChange={(e) => setAssignedDoctorCount(e.target.value)}
                  placeholder="Enter number"
                />
              </div>
              <div className="col-md-12">
                <label className="form-label">Filter by Doctor</label>
                <div className="d-flex flex-wrap gap-2">
                  {doctors.map((doctor) => (
                    <button
                      key={doctor.id}
                      type="button"
                      className={`btn btn-sm ${selectedDoctors.has(doctor.id) ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => toggleDoctorSelection(doctor.id)}
                    >
                      {doctor.name || doctor.email}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="row align-items-end g-3">
              <div className="col-md-6">
                <div>
                  <p className="mb-2"><strong>Selected Cases: {selectedCases.size}</strong></p>
                  <div className="btn-group" role="group">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={selectPageCases}
                      disabled={loading || paginatedCases.length === 0}
                    >
                      {paginatedCases.length > 0 && paginatedCases.every((caseItem) => selectedCases.has(caseItem.id))
                        ? 'Deselect Page'
                        : 'Select Page'}
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={selectAllCases}
                      disabled={loading || filteredCases.length === 0}
                    >
                      {selectedCases.size === filteredCases.length && filteredCases.length > 0
                        ? 'Deselect All'
                        : 'Select All'}
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setSelectedCases(new Set())}
                      disabled={selectedCases.size === 0}
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button
                    className="btn btn-outline-primary"
                    onClick={exportToCSV}
                    disabled={exporting || !canExport}
                  >
                    <i className="fas fa-file-csv me-2"></i>
                    Export to CSV
                  </button>
                  <button
                    className="btn btn-outline-success"
                    onClick={exportToExcel}
                    disabled={exporting || !canExport}
                  >
                    <i className="fas fa-file-excel me-2"></i>
                    Export to Excel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cases List and Preview */}
        <div className="row g-4 align-items-start">
          <div className="col-12 col-xl-7">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-success" role="status">
                  <span className="visually-hidden">Loading cases...</span>
                </div>
                <p className="mt-2">Loading cases...</p>
              </div>
            ) : filteredCases.length > 0 ? (
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-light">
                  <h5 className="mb-0">
                    <i className="fas fa-list me-2"></i>
                    Available Cases ({filteredCases.length})
                    {selectedCases.size > 0 && (
                      <small className="text-muted ms-2">({remainingCasesCount} remaining)</small>
                    )}
                  </h5>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={paginatedCases.length > 0 && paginatedCases.every((caseItem) => selectedCases.has(caseItem.id))}
                            onChange={selectPageCases}
                            title="Select current page cases"
                            ref={(input) => {
                              if (input) {
                                input.indeterminate = paginatedCases.some((caseItem) => selectedCases.has(caseItem.id)) && !paginatedCases.every((caseItem) => selectedCases.has(caseItem.id));
                              }
                            }}
                          />
                        </th>
                        <th>Case ID</th>
                        <th>Created Date</th>
                        <th>Status</th>
                        <th>Evaluations</th>
                        <th>Assigned Doctors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCases.map(caseItem => (
                        <tr key={caseItem.id}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedCases.has(caseItem.id)}
                              onChange={() => toggleCaseSelection(caseItem.id)}
                            />
                          </td>
                          <td>
                            <strong>{caseItem.id}</strong>
                          </td>
                          <td>
                            {caseItem.created_at
                              ? new Date(caseItem.created_at).toLocaleDateString()
                              : 'N/A'}
                          </td>
                          <td>
                            <span className={`badge bg-${caseItem.status === 'completed' ? 'success' : 'warning'}`}>
                              {caseItem.status || 'pending'}
                            </span>
                          </td>
                          <td>{caseItem.evaluation_count || 0}</td>
                          <td>
                            <span className="badge bg-secondary">
                              {caseItem.assignment_count || 0}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer bg-white border-top">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={goToPage}
                    showInfo={true}
                    totalItems={totalItems}
                    itemsPerPage={casesPerPage}
                  />
                </div>
              </div>
            ) : cases.length > 0 ? (
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center py-5">
                    <i className="fas fa-filter fa-4x text-muted mb-3"></i>
                    <h4>No cases match the selected doctor count</h4>
                    <p className="text-muted">Try clearing the doctor selection or disabling the case count filter.</p>
                  </div>
                </div>
              ) : (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="fas fa-inbox fa-4x text-muted mb-3"></i>
                  <h4>No Cases Available</h4>
                  <p className="text-muted">There are no cases available for export at this time.</p>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={loadCases}
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    Refresh
                  </button>
                </div>
              </div>
            )}
          </div>

          {canExport && (
            <div className="col-12 col-xl-5">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-info text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-eye me-2"></i>
                    Export Data Preview
                        {` (${Array.isArray(previewData) ? previewData.length : 0} rows)`}
                  </h5>
                </div>
                    {loadingPreview ? (
                  <div className="card-body text-center py-4">
                    <div className="spinner-border text-info" role="status">
                      <span className="visually-hidden">Loading preview...</span>
                    </div>
                    <p className="mt-2">Loading preview data...</p>
                  </div>
                    ) : exportType === 'all' ? (
                      <div>
                        <div style={{ fontSize: '12px', padding: '8px', backgroundColor: '#f0f8ff', borderBottom: '1px solid #dee2e6' }}>
                          <i className="fas fa-arrow-right me-2"></i>
                          <strong>Scroll right</strong> to see all columns (Questions, Patient Data, AI Results)
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '600px', overflow: 'auto' }}>
                          <table className="table table-sm table-striped mb-0">
                            <thead className="table-light sticky-top">
                              <tr>
                                <th style={{ minWidth: '90px' }}>Case ID</th>
                                <th style={{ minWidth: '140px' }}>Doctor ID</th>
                                <th style={{ minWidth: '140px' }}>Doctor Name</th>
                                {questionHeaders.length > 0 ? questionHeaders.map((header, index) => (
                                  <th key={`${header}-${index}`} style={{ minWidth: '220px' }}>{header || `Question ${index + 1}`}</th>
                                )) : (
                                  <th style={{ minWidth: '220px' }}>Question / Answer</th>
                                )}
                                <th style={{ minWidth: '80px' }}>Weight</th>
                                <th style={{ minWidth: '80px' }}>Height</th>
                                <th style={{ minWidth: '80px' }}>BMI</th>
                                <th style={{ minWidth: '80px' }}>Waist</th>
                                <th style={{ minWidth: '80px' }}>Hip</th>
                                <th style={{ minWidth: '80px' }}>Is Smoker</th>
                                <th style={{ minWidth: '80px' }}>Is Drinker</th>
                                <th style={{ minWidth: '120px' }}>Highest Education</th>
                                <th style={{ minWidth: '100px' }}>Lifestyle Level</th>
                                <th style={{ minWidth: '100px' }}>Sleep Level</th>
                                <th style={{ minWidth: '100px' }}>Stress Frequency</th>
                                <th style={{ minWidth: '80px' }}>Mood</th>
                                <th style={{ minWidth: '90px' }}>Resting HR</th>
                                <th style={{ minWidth: '90px' }}>Systolic BP</th>
                                <th style={{ minWidth: '90px' }}>Diastolic BP</th>
                                <th style={{ minWidth: '100px' }}>Blood Glucose</th>
                                <th style={{ minWidth: '100px' }}>Oxygen Saturation</th>
                                <th style={{ minWidth: '100px' }}>Completion %</th>
                                <th style={{ minWidth: '100px' }}>Physician Score</th>
                                <th style={{ minWidth: '100px' }}>AI Overall Score</th>
                                <th style={{ minWidth: '100px' }}>AI Color Category</th>
                                <th style={{ minWidth: '100px' }}>AI Physical Health</th>
                                <th style={{ minWidth: '100px' }}>AI Clinical Health</th>
                                <th style={{ minWidth: '100px' }}>AI Lifestyle</th>
                                <th style={{ minWidth: '100px' }}>AI Mental Health</th>
                                <th style={{ minWidth: '100px' }}>AI Completion %</th>
                                <th style={{ minWidth: '100px' }}>AI Risk Level</th>
                                {renderAiRawResponseHeaders()}
                              </tr>
                            </thead>
                            <tbody>
                              {rowsToRender.map((row, idx) => {
                                const weightVal = parseFloat(row.weight);
                                const heightVal = parseFloat(row.height);
                                let bmiValue = row.bmi || '';
                                if (!bmiValue && !Number.isNaN(weightVal) && !Number.isNaN(heightVal) && heightVal > 0) {
                                  let heightMeters = heightVal;
                                  if (heightMeters > 3) {
                                    heightMeters = heightMeters / 100.0;
                                  }
                                  if (heightMeters > 0) {
                                    bmiValue = (weightVal / (heightMeters * heightMeters)).toFixed(1);
                                  }
                                }
                                return (
                                  <tr key={idx}>
                                    <td><small>{row.case_id}</small></td>
                                    <td><small>{row.anonymized_doctor_id}</small></td>
                                    <td><small>{row.doctor_name}</small></td>
                                    {questionHeaders.length > 0 ? questionHeaders.map((header, headerIndex) => (
                                      <td key={`${row.case_id}-${header}-${headerIndex}`}><small>{row[header] || ''}</small></td>
                                    )) : (
                                      <td><small>{(row.qa_pairs || []).map((qa, qaIndex) => `${qa.question_text}: ${qa.answer}`).join(' | ')}</small></td>
                                    )}
                                    <td><small>{row.weight}</small></td>
                                    <td><small>{row.height}</small></td>
                                    <td><small>{bmiValue}</small></td>
                                    <td><small>{row.waist}</small></td>
                                    <td><small>{row.hip}</small></td>
                                    <td><small>{row.is_smoker}</small></td>
                                    <td><small>{row.is_drinker}</small></td>
                                    <td><small>{row.highest_education}</small></td>
                                    <td><small>{row.lifestyle_level}</small></td>
                                    <td><small>{row.sleep_level}</small></td>
                                    <td><small>{row.stress_frequency}</small></td>
                                    <td><small>{row.mood}</small></td>
                                    <td><small>{row.resting_heart_rate}</small></td>
                                    <td><small>{row.systolic_bp}</small></td>
                                    <td><small>{row.diastolic_bp}</small></td>
                                    <td><small>{row.blood_glucose}</small></td>
                                    <td><small>{row.oxygen_saturation}</small></td>
                                    <td><small>{row.patient_completion_percentage}</small></td>
                                    <td><small>{row.physician_score}</small></td>
                                    <td><small>{row.ai_overall_score}</small></td>
                                    <td><small>{row.ai_color_category}</small></td>
                                    <td><small>{row.ai_physical_health}</small></td>
                                    <td><small>{row.ai_clinical_health}</small></td>
                                    <td><small>{row.ai_lifestyle}</small></td>
                                    <td><small>{row.ai_mental_health}</small></td>
                                    <td><small>{row.ai_completion_percentage}</small></td>
                                    <td><small>{row.ai_risk_level}</small></td>
                                    {renderAiRawResponseCells(row, idx)}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          {rowsToRender.length > 0 && (
                            <div style={{ padding: '8px', fontSize: '12px', color: '#666', backgroundColor: '#f9f9f9', borderTop: '1px solid #dee2e6' }}>
                              Showing all {rowsToRender.length} preview rows. Full export will include all matching rows.
                            </div>
                          )}
                        </div>
                      </div>
                ) : Array.isArray(previewData) && previewData.length > 0 ? (
                  <div>
                    <div style={{ fontSize: '12px', padding: '8px', backgroundColor: '#f0f8ff', borderBottom: '1px solid #dee2e6' }}>
                      <i className="fas fa-arrow-right me-2"></i>
                      <strong>Scroll right</strong> to see Question & Answer columns, then Patient Data and AI Results
                    </div>
                    <div className="table-responsive" style={{ maxHeight: '400px', overflow: 'auto' }}>
                    {exportType === 'ai' ? (
                      <table className="table table-sm table-striped mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ minWidth: '90px' }}>Case ID</th>
                            <th style={{ minWidth: '120px' }}>AI Overall Score</th>
                            <th style={{ minWidth: '120px' }}>AI Color Category</th>
                            <th style={{ minWidth: '140px' }}>Physical Health</th>
                            <th style={{ minWidth: '140px' }}>Clinical Health</th>
                            <th style={{ minWidth: '140px' }}>Lifestyle</th>
                            <th style={{ minWidth: '140px' }}>Mental Health</th>
                            <th style={{ minWidth: '150px' }}>AI Completion %</th>
                            <th style={{ minWidth: '140px' }}>AI Risk Level</th>
                            {renderAiRawResponseHeaders()}
                            <th style={{ minWidth: '160px' }}>AI Assessment Created At</th>
                            <th style={{ minWidth: '160px' }}>AI Assessment Updated At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rowsToRender.map((row, idx) => (
                            <tr key={idx}>
                              <td><small>{row.case_id}</small></td>
                              <td><small><strong>{row.ai_overall_score}</strong></small></td>
                              <td><small>{row.ai_color_category}</small></td>
                              <td><small>{row.ai_physical_health}</small></td>
                              <td><small>{row.ai_clinical_health}</small></td>
                              <td><small>{row.ai_lifestyle}</small></td>
                              <td><small>{row.ai_mental_health}</small></td>
                              <td><small>{row.ai_completion_percentage}</small></td>
                              <td><small>{row.ai_risk_level}</small></td>
                              {renderAiRawResponseCells(row, idx)}
                              <td><small>{row.ai_assessment_created_at}</small></td>
                              <td><small>{row.ai_assessment_updated_at}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : exportType === 'cases' ? (
                      <table className="table table-sm table-striped mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ minWidth: '90px' }}>Case ID</th>
                            <th style={{ minWidth: '80px' }}>Weight</th>
                            <th style={{ minWidth: '80px' }}>Height</th>
                            <th style={{ minWidth: '80px' }}>Waist</th>
                            <th style={{ minWidth: '80px' }}>Hip</th>
                            <th style={{ minWidth: '80px' }}>Smoker</th>
                            <th style={{ minWidth: '80px' }}>Drinker</th>
                            <th style={{ minWidth: '100px' }}>Education</th>
                            <th style={{ minWidth: '100px' }}>Lifestyle</th>
                            <th style={{ minWidth: '100px' }}>Sleep Level</th>
                            <th style={{ minWidth: '80px' }}>Mood</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rowsToRender.map((row, idx) => (
                            <tr key={idx}>
                              <td><small>{row.case_id}</small></td>
                              <td><small>{row.weight}</small></td>
                              <td><small>{row.height}</small></td>
                              <td><small>{row.waist}</small></td>
                              <td><small>{row.hip}</small></td>
                              <td><small>{row.is_smoker}</small></td>
                              <td><small>{row.is_drinker}</small></td>
                              <td><small>{row.highest_education}</small></td>
                              <td><small>{row.lifestyle_level}</small></td>
                              <td><small>{row.sleep_level}</small></td>
                              <td><small>{row.mood}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : exportType === 'evaluations' ? (
                      <table className="table table-sm table-striped mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ minWidth: '90px' }}>Case ID</th>
                            <th style={{ minWidth: '160px' }}>Doctor Name</th>
                            <th style={{ minWidth: '120px' }}>Doctor ID</th>
                            <th style={{ minWidth: '100px' }}>Status</th>
                            <th style={{ minWidth: '100px' }}>Questions</th>
                            <th style={{ minWidth: '120px' }}>Protocol</th>
                            <th style={{ minWidth: '100px' }}>Avg Time</th>
                            <th style={{ minWidth: '100px' }}>Time Spent</th>
                            <th style={{ minWidth: '100px' }}>Total Time</th>
                            <th style={{ minWidth: '140px' }}>Assigned At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rowsToRender.map((row, idx) => (
                            <tr key={idx}>
                              <td><small>{row.case_id}</small></td>
                              <td><small>{row.doctor_name || row.anonymized_doctor_id || 'Unknown Doctor'}</small></td>
                              <td><small>{row.anonymized_doctor_id}</small></td>
                              <td><small>{row.evaluation_status}</small></td>
                              <td><small>{row.questions_answered}</small></td>
                              <td><small>{row.protocol_name}</small></td>
                              <td><small>{row.avg_time_per_question}</small></td>
                              <td><small>{row.total_time_seconds}</small></td>
                              <td><small>{row.total_time_seconds}</small></td>
                              <td><small>{row.evaluation_assigned_at}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : exportType === 'doctor_ai' ? (
                      <table className="table table-sm table-striped mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ minWidth: '90px' }}>Case ID</th>
                            <th style={{ minWidth: '160px' }}>Doctor Name</th>
                            <th style={{ minWidth: '120px' }}>Doctor ID</th>
                            <th style={{ minWidth: '100px' }}>Status</th>
                            <th style={{ minWidth: '100px' }}>Questions</th>
                            <th style={{ minWidth: '120px' }}>Protocol</th>
                            <th style={{ minWidth: '100px' }}>Avg Time</th>
                            <th style={{ minWidth: '100px' }}>Time Spent</th>
                            <th style={{ minWidth: '90px' }}>AI Score</th>
                            <th style={{ minWidth: '120px' }}>AI Category</th>
                            <th style={{ minWidth: '120px' }}>AI Risk</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rowsToRender.map((row, idx) => (
                            <tr key={idx}>
                              <td><small>{row.case_id}</small></td>
                              <td><small>{row.doctor_name || row.anonymized_doctor_id || 'Unknown Doctor'}</small></td>
                              <td><small>{row.anonymized_doctor_id}</small></td>
                              <td><small>{row.evaluation_status}</small></td>
                              <td><small>{row.questions_answered}</small></td>
                              <td><small>{row.protocol_name}</small></td>
                              <td><small>{row.avg_time_per_question}</small></td>
                              <td><small>{row.total_time_seconds}</small></td>
                              <td><small>{row.ai_overall_score}</small></td>
                              <td><small>{row.ai_color_category}</small></td>
                              <td><small>{row.ai_risk_level}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
) : (
                      <table className="table table-sm table-striped mb-0">
                        <thead className="table-light sticky-top">
                            <tr>
                            <th style={{ minWidth: '90px' }}>Case ID</th>
                            <th style={{ minWidth: '160px' }}>Doctor Name</th>
                            <th style={{ minWidth: '140px' }}>Doctor ID</th>
                            {questionHeaders.length > 0 ? questionHeaders.map((header, index) => (
                              <th key={`${header}-${index}`} style={{ minWidth: '180px' }}>{header || `Question ${index + 1}`}</th>
                            )) : (
                              <th style={{ minWidth: '220px' }}>Q/A Preview</th>
                            )}
                            <th style={{ minWidth: '80px' }}>Weight</th>
                            <th style={{ minWidth: '80px' }}>Height</th>
                            <th style={{ minWidth: '80px' }}>BMI</th>
                            <th style={{ minWidth: '80px' }}>Waist</th>
                            <th style={{ minWidth: '80px' }}>Hip</th>
                            <th style={{ minWidth: '80px' }}>Is Smoker</th>
                            <th style={{ minWidth: '80px' }}>Is Drinker</th>
                            <th style={{ minWidth: '120px' }}>Highest Education</th>
                            <th style={{ minWidth: '100px' }}>Lifestyle Level</th>
                            <th style={{ minWidth: '100px' }}>Sleep Level</th>
                            <th style={{ minWidth: '100px' }}>Stress Frequency</th>
                            <th style={{ minWidth: '80px' }}>Mood</th>
                            <th style={{ minWidth: '90px' }}>Resting HR</th>
                            <th style={{ minWidth: '90px' }}>Systolic BP</th>
                            <th style={{ minWidth: '90px' }}>Diastolic BP</th>
                            <th style={{ minWidth: '100px' }}>Blood Glucose</th>
                            <th style={{ minWidth: '100px' }}>Oxygen Saturation</th>
                            <th style={{ minWidth: '100px' }}>Completion %</th>
                            <th style={{ minWidth: '100px' }}>Physician Score</th>
                            <th style={{ minWidth: '100px' }}>AI Overall Score</th>
                            <th style={{ minWidth: '100px' }}>AI Color Category</th>
                            <th style={{ minWidth: '100px' }}>AI Physical Health</th>
                            <th style={{ minWidth: '100px' }}>AI Clinical Health</th>
                            <th style={{ minWidth: '100px' }}>AI Lifestyle</th>
                            <th style={{ minWidth: '100px' }}>AI Mental Health</th>
                            <th style={{ minWidth: '100px' }}>AI Completion %</th>
                            <th style={{ minWidth: '100px' }}>AI Risk Level</th>
                            {renderAiRawResponseHeaders()}
                            </tr>
                          </thead>
                          <tbody>
                            {rowsToRender.map((row, idx) => {
                              const weightVal = parseFloat(row.weight);
                              const heightVal = parseFloat(row.height);
                              let bmiValue = row.bmi || '';
                              if (!bmiValue && !Number.isNaN(weightVal) && !Number.isNaN(heightVal) && heightVal > 0) {
                                let heightMeters = heightVal;
                                if (heightMeters > 3) {
                                  heightMeters = heightMeters / 100.0;
                                }
                                if (heightMeters > 0) {
                                  bmiValue = (weightVal / (heightMeters * heightMeters)).toFixed(1);
                                }
                              }
                              return (
                                <tr key={idx}>
                                  <td><small>{row.case_id}</small></td>
                                  <td><small>{row.doctor_name || row.anonymized_doctor_id || 'Unknown Doctor'}</small></td>
                                  <td><small>{row.anonymized_doctor_id}</small></td>
                                  {questionHeaders.length > 0 ? questionHeaders.map((header, headerIndex) => (
                                    <td key={`${row.case_id}-${header}-${headerIndex}`}><small>{row[header] || (row.question_text === header ? row.answer : '') || ''}</small></td>
                                  )) : (
                                    <td>
                                      <div className="small text-wrap">
                                        <span className="text-muted">No question headers available</span>
                                      </div>
                                    </td>
                                  )}
                                  <td><small>{row.weight}</small></td>
                                  <td><small>{row.height}</small></td>
                                  <td><small>{bmiValue}</small></td>
                                  <td><small>{row.waist}</small></td>
                                  <td><small>{row.hip}</small></td>
                                  <td><small>{row.is_smoker}</small></td>
                                  <td><small>{row.is_drinker}</small></td>
                                  <td><small>{row.highest_education}</small></td>
                                  <td><small>{row.lifestyle_level}</small></td>
                                  <td><small>{row.sleep_level}</small></td>
                                  <td><small>{row.stress_frequency}</small></td>
                                  <td><small>{row.mood}</small></td>
                                  <td><small>{row.resting_heart_rate}</small></td>
                                  <td><small>{row.systolic_bp}</small></td>
                                  <td><small>{row.diastolic_bp}</small></td>
                                  <td><small>{row.blood_glucose}</small></td>
                                  <td><small>{row.oxygen_saturation}</small></td>
                                  <td><small>{row.patient_completion_percentage}</small></td>
                                  <td><small>{row.physician_score}</small></td>
                                  <td><small>{row.ai_overall_score}</small></td>
                                  <td><small>{row.ai_color_category}</small></td>
                                  <td><small>{row.ai_physical_health}</small></td>
                                  <td><small>{row.ai_clinical_health}</small></td>
                                  <td><small>{row.ai_lifestyle}</small></td>
                                  <td><small>{row.ai_mental_health}</small></td>
                                  <td><small>{row.ai_completion_percentage}</small></td>
                                  <td><small>{row.ai_risk_level}</small></td>
                                  {renderAiRawResponseCells(row, idx)}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                    )}
                    </div>
                  </div>
                ) : (
                  <div className="card-body text-center py-4">
                    <p className="text-muted mb-0">No evaluation data found for the selected filters</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Export Information */}
        <div className="row mt-5">
          <div className="col-lg-8">
            <div className="card border-0 bg-light">
              <div className="card-body">
                <h6 className="card-title">
                  <i className="fas fa-info-circle text-info me-2"></i>
                  Export Information
                </h6>
                <ul className="small text-muted mb-0">
                  <li><strong>Doctor Evaluations (Detail):</strong> One row per case per doctor with questions as columns - shows Q/A pairs in preview</li>
                  <li><strong>Doctor Evaluations (Summary):</strong> One row per doctor per case - aggregated statistics (questions answered, time spent)</li>
                  <li><strong>Cases Only:</strong> One row per case - patient demographics and health measurements</li>
                  <li><strong>AI Results Only:</strong> One row per case - AI predictions and risk assessments</li>
                  <li><strong>Doctor Evaluations + AI Summary:</strong> One row per doctor per case with AI predictions included</li>
                  <li><strong>All Three Tables (Combined):</strong> Exports a single combined table with question-level doctor evaluations and AI results</li>
                  <li>Select one, multiple, or all cases before exporting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default StatisticalReportsPage;